import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	ModalComponent,
	FormGroupComponent,
	InputComponent,
	SelectComponent,
	AlertComponent,
	ITagImage,
} from '@tecalisdev/infinity';
import { MasterService } from '../../../../../shared/services/master.service';
import { ReactiveFormsService } from '../../../../../shared/services/reactive-forms.service';
import { SnackbarService } from '../../../../../shared/services/snackbar.service';
import { IApiKeyFormPayload, IApikeyListBody } from '../../models/apikeys.models';
import { HttpErrorResponse } from '@angular/common/http';
import { ApikeyService } from '../../services/apikey.service';

@Component({
	selector: 'app-apikey-form',
	imports: [
		ModalComponent,
		ReactiveFormsModule,
		TranslatePipe,
		FormGroupComponent,
		InputComponent,
		SelectComponent,
		AlertComponent,
	],
	templateUrl: './apikey-form.component.html',
	styleUrl: './apikey-form.component.scss',
})
export class ApikeyFormComponent implements OnInit {
	masterService = inject(MasterService);
	reactiveFormService = inject(ReactiveFormsService);
	trans = inject(TranslateService);
	snackbarService = inject(SnackbarService);
	apikeyService = inject(ApikeyService);

	@Input() detail: IApikeyListBody;
	@Input() company: string;
	@Output() closeEvent = new EventEmitter<boolean>();

	public readonly modalTagImage: ITagImage = { icon: 'icon-lock' };
	public readonly isSaving = signal(false);
	/** Copia estable: el getter de MasterService crea array nuevo cada CD y el select resetea el search. */
	public readonly companyOptions = this.masterService.companies ?? [];
	public errorMsg: string = '';
	public form: FormGroup = new FormGroup({
		key: new FormControl(''),
		alias: new FormControl('', Validators.required),
		company_uuid: new FormControl('', Validators.required),
	});

	get primaryButtonLabel(): string {
		if (this.isSaving()) {
			return this.trans.instant('BUTTON.SAVING');
		}
		return this.trans.instant('BUTTON.SAVE');
	}

	ngOnInit(): void {
		if (this.companyOptions.length === 1) {
			this.form.get('company_uuid').setValue(this.companyOptions[0]);
		}
		if (this.detail) {
			this.form.setValue({
				key: this.detail.key,
				alias: this.detail.alias,
				company_uuid: this.resolveCompanyOption(this.detail.company_uuid),
			});
			// Si hay `detail` (edición), hacer que `key` sea obligatorio.
			this.form.get('key').setValidators(Validators.required);
			this.form.get('key').updateValueAndValidity();
		} else {
			// Si no hay `detail` (creación), quitar la validación `required` del key.
			this.form.get('key').clearValidators();
			this.form.get('key').updateValueAndValidity();
		}
		if (this.company) {
			this.form.get('company_uuid').setValue(this.resolveCompanyOption(this.company));
		}
	}

	/** Resuelve uuid/string a opción ISelect del master para que el select filtre y muestre bien. */
	private resolveCompanyOption(companyUuid: string | { id?: string } | null | undefined) {
		const id =
			typeof companyUuid === 'object' && companyUuid != null
				? companyUuid.id
				: companyUuid;
		return this.companyOptions.find((c) => c.id === id) ?? companyUuid;
	}

	public closeModal(type: string) {
		if (this.isSaving()) {
			return;
		}
		const actions = {
			buttonPrimary: () => {
				if (this.form.invalid) {
					this.form.markAllAsTouched();
					return;
				}
				this.detail ? this.updateApikey() : this.createApikey();
			},
		};

		actions[type] ? actions[type]() : this.closeEvent.emit(false);
	}

	private updateApikey(): void {
		this.isSaving.set(true);
		this.apikeyService.update(this.detail.uuid, this.getPayload()).subscribe({
			next: () => {
				this.closeEvent.emit(true);
				this.snackbarService.show(this.trans.instant('API_KEYS.LIST.UPDATE_CONFIRM'));
			},
			error: (_error: HttpErrorResponse) => {
				this.errorMsg = this.trans.instant('API_KEYS.LIST.UPDATE_ERROR');
				this.isSaving.set(false);
			},
		});
	}

	private createApikey(): void {
		this.isSaving.set(true);
		this.apikeyService.create(this.getPayload()).subscribe({
			next: () => {
				this.closeEvent.emit(true);
				this.snackbarService.show(this.trans.instant('API_KEYS.LIST.CREATE_CONFIRM'));
			},
			error: (_error: HttpErrorResponse) => {
				this.errorMsg = this.trans.instant('API_KEYS.LIST.CREATE_ERROR');
				this.isSaving.set(false);
			},
		});
	}

	private getPayload(): IApiKeyFormPayload {
		return {
			key: this.form.get('key').value,
			alias: this.form.get('alias').value?.id ?? this.form.get('alias').value,
			company_uuid: this.form.get('company_uuid').value?.id ?? this.form.get('company_uuid').value,
			enabled: this.detail ? this.detail.enabled : true,
		};
	}
}
